/**
 * Error messages to ignore - typically browser extensions, third-party scripts, and known false positives
 */
export const SENTRY_IGNORE_ERRORS: (string | RegExp)[] = [
	// Browser extensions
	/^chrome-extension:\/\//,
	/^moz-extension:\/\//,
	/^safari-extension:\/\//,
	/extensions\//i,

	// Common browser/network noise
	'ResizeObserver loop limit exceeded',
	'ResizeObserver loop completed with undelivered notifications',
	'Non-Error promise rejection captured with value: Object Not Found Matching Id',
	'Non-Error promise rejection captured',
	'Load failed',
	'Failed to fetch',
	'NetworkError when attempting to fetch resource',
	'Network request failed',
	'AbortError',
	'The operation was aborted',
	'cancelled',
	'canceled',

	// Third-party script errors
	/^Script error\.?$/,
	'Script error.',
	/instantpage/i,
	/gtag/,
	/analytics/i,
	/hotjar/i,
	/intercom/i,
	/facebook/i,
	/fbevents/i,
	/clarity/i,

	// Next.js specific noise
	'NEXT_NOT_FOUND',
	'NEXT_REDIRECT',
	/Hydration failed/,
	/Text content does not match/,
	/There was an error while hydrating/,
	// RSC navigation: thrown by Next when an old client (or a bot/CDN replay) sends a
	// router-state header the new server can't parse. Next serves the full page anyway;
	// the exception is logged-not-fatal noise.
	'The router state header was sent but could not be parsed',

	// Common user-caused errors
	'ChunkLoadError',
	/Loading chunk \d+ failed/,
	/Loading CSS chunk \d+ failed/,
];

/**
 * Browser extension URL patterns to ignore (for filename checks in beforeSend)
 */
export const BROWSER_EXTENSION_PATTERNS = [
	'chrome-extension://',
	'moz-extension://',
	'safari-extension://',
	'extensions/',
] as const;

/**
 * URL patterns to ignore - typically third-party scripts and CDNs
 */
export const SENTRY_DENY_URLS: (string | RegExp)[] = [
	// Browser extensions
	/^chrome-extension:\/\//,
	/^moz-extension:\/\//,
	/^safari-extension:\/\//,

	// Third-party scripts
	/google-analytics\.com/,
	/googletagmanager\.com/,
	/facebook\.net/,
	/connect\.facebook\.net/,
	/hotjar\.com/,
	/intercom\.io/,
	/clarity\.ms/,
	/cdn\.segment\.com/,
	/apis\.google\.com/,
];

/**
 * HTTP status codes considered critical (should trigger immediate alerts)
 */
export const CRITICAL_HTTP_STATUS_CODES = [500, 502, 503, 504];

/**
 * Sentry severity levels for different error types
 */
export const ERROR_SEVERITY = {
	CRITICAL: 'fatal', // 5xx errors, app crashes
	HIGH: 'error', // Auth failures, network errors
	MEDIUM: 'warning', // Other 4xx client errors
	LOW: 'info', // Expected errors, handled gracefully
} as const;

export type TErrorSeverity = (typeof ERROR_SEVERITY)[keyof typeof ERROR_SEVERITY];

export const API_ROUTES_TO_IGNORE = ['/chat/poll-messages'];

/**
 * Web Vitals thresholds for alerting (values in milliseconds unless noted)
 * Based on Google's Core Web Vitals recommendations with alerting thresholds
 */
export const PERFORMANCE_THRESHOLDS = {
	/** Largest Contentful Paint - should be < 2.5s, alert at > 4s */
	LCP_ALERT_MS: 4000,

	/** First Input Delay - should be < 100ms, alert at > 300ms */
	FID_ALERT_MS: 300,

	/** Cumulative Layout Shift - should be < 0.1, alert at > 0.25 (unitless) */
	CLS_ALERT: 0.25,

	/** First Contentful Paint - alert at > 3s */
	FCP_ALERT_MS: 3000,

	/** Time to First Byte - alert at > 1.8s */
	TTFB_ALERT_MS: 1800,

	/** Total page load time - alert at > 10s */
	PAGE_LOAD_ALERT_MS: 10000,

	/** Extreme page load (critical) - alert at > 30s */
	PAGE_LOAD_CRITICAL_MS: 30000,
} as const;

/**
 * Sample rates for Sentry tracing (0.0 to 1.0)
 */
export const TRACE_SAMPLE_RATES = {
	/** Always capture error and slow transactions */
	ALWAYS: 1.0,
	/** Page navigations */
	PAGE_NAVIGATION: 0.25,
	/** Default for everything else */
	DEFAULT: 0.1,
} as const;

/**
 * Sample rates for Sentry session replay
 */
export const REPLAY_SAMPLE_RATES = {
	/** Normal sessions (0 = disabled) */
	SESSION: 0,
	/** Sessions where an error occurred. Lowered from 1.0 — full replays are
	 * ~100s of KB each. Errors themselves are still captured at 100%; we just
	 * sample which ones include a video replay. */
	ERROR_SESSION: 0.2,
	/** Profiling sample rate */
	PROFILING: 0.05,
} as const;

/**
 * Error categories for tagging in Sentry
 */
/**
 * Client-side event cap. Removing the occurrence-threshold batcher means event volume
 * now tracks the raw API-error rate, so a backend outage or an auth-failure loop could
 * exhaust the org quota — after which Sentry drops events indiscriminately, including
 * the novel ones. This caps repeats of the SAME issue while always letting the first
 * occurrence through, so nothing is hidden and a flood stays bounded.
 */
export const EVENT_RATE_LIMIT = {
	/** Rolling window for the per-key cap. */
	WINDOW_MS: 60 * 1000,
	/** Max events per identical issue key per window. */
	MAX_PER_KEY: 5,
	/** Absolute ceiling across all keys per window, for a broad multi-endpoint failure. */
	MAX_TOTAL: 50,
} as const;

export const ERROR_CATEGORIES = {
	CHUNK_LOAD: 'chunk_load_error',
	HYDRATION: 'hydration_error',
	UNHANDLED_5XX: '5xx_unhandled',
} as const;

/**
 * Fingerprints for grouping similar errors in Sentry
 */
export const ERROR_FINGERPRINTS = {
	CHUNK_LOAD: 'chunk-load-error',
	HYDRATION: 'hydration-error',
} as const;

/**
 * URLs to ignore in breadcrumbs (analytics/tracking scripts)
 */
export const IGNORED_BREADCRUMB_URLS = [
	'google-analytics',
	'segment',
	'mixpanel',
	'hotjar',
	'clarity',
] as const;

/**
 * Console log levels to keep in breadcrumbs
 */
export const ALLOWED_CONSOLE_LEVELS = ['warn', 'error'] as const;
